export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const PHONE_PATTERN = /^\+?[0-9\s().-]{7,20}$/;

export const first_present = (values, fallback = "") => values.find((value) => value) || fallback;
export const first_array = (values) => values.find((value) => Array.isArray(value)) || [];

export function parse_json_or_null(value) {
  let parsed = null;
  try {
    parsed = JSON.parse(value);
  } catch {
    parsed = null;
  }
  return parsed;
}

export function member_status(member) {
  return String(member?.status || member?.invite_status || member?.invitation_status || "").toLowerCase();
}

export function member_is_pending(member) {
  const status = member_status(member);
  return [
    status.includes("pending"),
    member?.pending_invite === true,
    member?.is_pending === true,
    member?.invite_accepted === false,
  ].some(Boolean);
}

export function member_is_active(member) {
  if (member_is_pending(member)) return false;

  const status = member_status(member);
  return [
    member?.is_active === true,
    member?.user?.is_active === true,
    status === "active",
    !status && member?.is_active !== false && member?.user?.is_active !== false,
  ].some(Boolean);
}

export function normalize_member(member) {
  const source = member || {};
  const user = source.user || source;
  const normalized_member = {
    ...source,
    id: first_present([user.id, source.id], null),
    first_name: first_present([user.first_name, source.first_name]),
    last_name: first_present([user.last_name, source.last_name]),
    email: first_present([user.email, source.email]),
    role: first_present([user.role, source.role], null),
    positions: first_array([
      source.positions,
      source.organization_positions,
      source.member_positions,
      user.positions,
    ]),
    permissions: first_array([
      source.permissions,
      source.organization_permissions,
      source.member_permissions,
      user.permissions,
    ]),
    profile_picture: first_present([user.profile_picture, source.profile_picture], null),
    profile: first_present([user.profile, source.profile], null),
    user,
  };
  return normalized_member;
}

export function collect_validation_errors(rules) {
  return rules.reduce((errors, rule) => {
    if (rule.is_invalid()) errors[rule.field] = rule.message;
    return errors;
  }, {});
}

export function create_empty_bulk_member() {
  return Object.fromEntries([
    ["first_name", ""],
    ["last_name", ""],
    ["email", ""],
    ["role", "user"],
  ]);
}

function clean_bulk_member_value(value) {
  return String(value ?? "").trim();
}

function get_bulk_member_role(role) {
  return role || null;
}

export function normalize_bulk_member(member = {}) {
  return {
    first_name: clean_bulk_member_value(member.first_name),
    last_name: clean_bulk_member_value(member.last_name),
    email: clean_bulk_member_value(member.email),
    role: get_bulk_member_role(member.role),
  };
}

function count_bulk_member_emails(members) {
  return members.reduce((counts, member) => {
    const email = member.email.toLowerCase();
    if (email) counts.set(email, (counts.get(email) || 0) + 1);
    return counts;
  }, new Map());
}

function validate_bulk_member(member, email_counts) {
  const email = member.email.toLowerCase();
  return collect_bulk_member_validation_errors([
    { field: "first_name", invalid: !member.first_name, message: "First name is required." },
    { field: "last_name", invalid: !member.last_name, message: "Last name is required." },
    { field: "email", invalid: !member.email, message: "Email is required." },
    { field: "email", invalid: has_invalid_bulk_member_email(member.email), message: "Please enter a valid email address." },
    { field: "email", invalid: has_duplicate_bulk_member_email(email, email_counts), message: "Email is already listed." },
    { field: "role", invalid: !member.role, message: "User role is required." },
  ]);
}

function collect_bulk_member_validation_errors(rules) {
  return rules.reduce((errors, rule) => {
    if (rule.invalid) errors[rule.field] = rule.message;
    return errors;
  }, {});
}

function has_invalid_bulk_member_email(email) {
  return Boolean(email) && !EMAIL_PATTERN.test(email);
}

function has_duplicate_bulk_member_email(email, email_counts) {
  return Boolean(email) && email_counts.get(email) > 1;
}

export function validate_bulk_member_entries(members, organization_id) {
  const normalized_members = members.map(normalize_bulk_member);
  const email_counts = count_bulk_member_emails(normalized_members);
  const errors = normalized_members.map((member) => validate_bulk_member(member, email_counts));
  const form_error = organization_id ? "" : "Organisation id is missing. Please refresh and try again.";
  const valid = !form_error && errors.every((member_errors) => !Object.keys(member_errors).length);

  return {
    valid,
    errors: {
      form: form_error,
      members: errors,
    },
  };
}

export function build_bulk_member_payload(organization_id, members) {
  return {
    organization_id,
    members: members.map(normalize_bulk_member),
  };
}

export function format_bulk_member_error(response = {}) {
  const emails = Array.isArray(response.emails) && response.emails.length
    ? ` ${response.emails.join(", ")}`
    : "";
  return `${response.message || "Could not add organisation members."}${emails}`.trim();
}

export async function add_members_individually(payload, request_add_member) {
  const created_members = [];
  for (const member of payload.members) {
    const response = await request_add_member({
      organization_id: payload.organization_id,
      ...member,
    });
    if (response?.status !== "success") {
      throw new Error(response?.message || "Could not add organisation members.");
    }
    created_members.push(response.member);
  }
  return created_members;
}

function assert_bulk_member_response(response) {
  if (response?.status === "success") return response;
  throw new Error(response?.message || "Could not add organisation members.");
}

function is_missing_bulk_route(error) {
  return error?.status === 404;
}

function get_bulk_response_member_count(response, fallback_count) {
  return response?.members?.length || fallback_count;
}

function normalize_bulk_submission_count(members, fallback_count) {
  if (!Array.isArray(members)) return members;
  return members.length || fallback_count;
}

function add_members_for_missing_bulk_route(error, payload, request_add_member) {
  if (!is_missing_bulk_route(error)) return Promise.reject(error);
  return add_members_individually(payload, request_add_member);
}

export function submit_bulk_members(payload, request_bulk_members, request_add_member) {
  return request_bulk_members(payload)
    .then(assert_bulk_member_response)
    .then((response) => get_bulk_response_member_count(response, payload.members.length))
    .catch((error) => add_members_for_missing_bulk_route(error, payload, request_add_member))
    .then((members) => normalize_bulk_submission_count(members, payload.members.length));
}

export function get_member_positions(member) {
  const positions = member?.positions || [];
  return Array.isArray(positions) ? positions : [];
}

export function get_member_primary_position_id(member) {
  const position = get_member_positions(member)[0];
  return position?.id || position?.position_id || position?.organization_position_id || "";
}

export function get_member_permission_ids(member) {
  const permissions = member?.permissions || [];
  if (!Array.isArray(permissions)) return [];
  return permissions.map((permission) => permission.id || permission.code || permission.name || permission).filter(Boolean);
}

export function get_initials(member) {
  const first = member?.first_name?.charAt(0) || "";
  const last = member?.last_name?.charAt(0) || "";
  return `${first}${last}`.toUpperCase() || "U";
}

export function get_member_profile_picture(member) {
  if (!member) return null;
  const profile_files = member.user?.profile?.profile_files || member.profile?.profile_files || [];
  const profile_picture = profile_files.find((file) => file.profile_file_type === "profile_picture");
  return first_present([
    member.profile_picture,
    member.user?.profile_picture,
    profile_picture?.file?.s3_url,
    profile_picture?.file?.url,
    profile_picture?.url,
  ], null);
}
